import { useState, useEffect, useContext } from "react";
import { Outlet } from "react-router-dom";
import axios from "axios";
import { ethers } from "ethers";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";

const { ethereum } = window;

export const getContract = async (url: string, address: string, isAbiFile?: boolean) => {
  if (!ethereum)
    return console.log("No ethereum found while getting contracts");

  try {
    const contractFile = await fetch(url);
    let convertContractFileToJson: any = null;
    if (isAbiFile) {
      const abi = await contractFile.json();
      convertContractFileToJson = abi;
    } else {
      const { abi } = await contractFile.json();
      convertContractFileToJson = abi;
    }

    const provider = new ethers.providers.Web3Provider(ethereum);
    const signer = provider.getSigner();

    return new ethers.Contract(address, convertContractFileToJson, signer);
  } catch (error) {
    console.log(error);
  }
};

const updateNetWork = async () => {
  if (!ethereum)
    return toast.error(
      "Metamask is not installed, please install it to complete the process"
    );

  const chainId = `0x${Number(process.env.REACT_APP_CHAIN_ID).toString(16)}`;

  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId }],
    });

    console.log("Metamask switched to the correct network successfully");
  } catch (error) {
    console.log("Chain not found we will add it...");

    try {
      await ethereum.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainName: process.env.REACT_APP_CHAIN_NAME,
            chainId,
            rpcUrls: [process.env.REACT_APP_CHAIN_RPC_URL],
            nativeCurrency: {
              name: process.env.REACT_APP_CHAIN_CURRENCY_SYMBOL,
              symbol: process.env.REACT_APP_CHAIN_CURRENCY_SYMBOL,
              decimals: 18,
            },
            blockExplorerUrls: [process.env.REACT_APP_CHAIN_BLOCK_EXPLORER_URL],
          },
        ],
      });

      console.log("Metamask Added the correct network successfully");
    } catch (error) {
      console.log(
        "Chain not found and we couldn't add it, please add it manually"
      );
    }
  }
};

export const connectWallet = async (updateState: any) => {
  if (!ethereum)
    return toast.error("Need to install MetaMask to connect to the wallet");
  try {
    await updateNetWork();
    const address = await ethereum.request({ method: "eth_requestAccounts" });

    updateState({ 
      connectedAddress: address[0],
      isAdminAddress: address[0].toLowerCase() === process.env.REACT_APP_ADMIN_ADDRESS?.toLowerCase(),
    });
  } catch (error) {
    console.log("error while updating network: ", error);
  }
};

export default function SmartContractsLayout() {
  const { user, isAdminAddress, connectedAddress, setState: updateContextState } =
    useContext(AppContext);
  const [state, setState] = useState<any>({
    user: {
      address: ethereum?.selectedAddress || "",
      balance: "0.0000",
    },
    contracts: {
      vesting: {
        contract: null,
        data: null,
      },
      claimer: {
        contract: null,
        data: null,
      },
      bullit: {
        contract: null,
        data: {
          decimals: "16"
        },
      }
    },
  });

  const getInitData = async () => {
    try {
      const contract = await getContract(
        "/abi/Vesting.json",
        process.env.REACT_APP_VESTING_CONTRACT_ADDRESS as string
      );
      const claimerContract = await getContract(
        "/abi/PresaleV2.json",
        process.env.REACT_APP_PRESALE_V2_CONTRACT_ADDRESS as string
      );

      const bullitContract: any = await getContract(
        "/abi/Bullit.json",
        process.env.REACT_APP_BULLIT_CONTRACT_ADDRESS as string,
        true
      );
      const decimals = await bullitContract.decimals();

      setState({
        ...state,
        contracts: {
          ...state.contracts,
          vesting: { ...state.contracts.vesting.data, contract },
          claimer: {
            ...state.contracts.claimer.data,
            contract: claimerContract,
          },
          bullit: {
            data: {
              ...state.contracts.bullit.data,
              decimals,
            },
            contract: bullitContract,
          }
        },
      });
    } catch (error: any) {
      throw Error(error);
    }
  };

  useEffect(() => {
    if (!connectedAddress) return;

    getInitData().catch((error) => console.error(error));
  }, [connectedAddress]);

  useEffect(() => {
    if (!ethereum) return;

    ethereum.on("accountsChanged", (acc: string[]) => {
      if (acc.length === 0) return updateContextState({ connectedAddress: "" });

      setState({
        ...state,
        user: { ...state.user, address: acc[0] },
      });

      updateContextState({
        connectedAddress: acc[0],
        isAdminAddress:
          acc[0]?.toLowerCase() ===
          process.env.REACT_APP_ADMIN_ADDRESS?.toLowerCase(),
      });
    });

    ethereum.on("chainChanged", (chainId: string) => {
      updateNetWork();
    });

    ethereum.on("disconnect", () => {
      console.log("Disconnected from MetaMask");
      updateContextState({ connectedAddress: "" });
    });
  }, []);

  const updateClaimers = async () => {
    if (!user.permissions.includes("update_claimers")) return toast.error("you are not allowed to do this");
    if (!connectedAddress) return connectWallet(updateContextState);
    if (!state.contracts.claimer.contract) return toast.error("no contract found");
    if (!isAdminAddress) return toast.error("you are not allowed to do this");

    let totalPages = 0;
    let perPage = 50;
    let index = 0;
    let result = [];

    do {
      let value = await state.contracts.claimer.contract.getClaimers(
        index,
        perPage
      );
      totalPages = value.totalListed;

      result = value.claimers_.map((item: any) => ({
        claimerAddress: item.claimerAddress,
        balance: (item.balance.toString() / Math.pow(10, 16)).toLocaleString(
          "fullwide",
          { useGrouping: false }
        ),
        availableClaim: (item.availableClaim / Math.pow(10, 16)).toLocaleString(
          "fullwide",
          { useGrouping: false }
        ),
        totalClaimed: (
          item.totalClaimed.toString() / Math.pow(10, 16)
        ).toLocaleString("fullwide", { useGrouping: false }),
      }));

      index++;
    } while (index < Math.ceil(totalPages / perPage));
    try {
      await axios.post("/claimers/update-claimers", { claimers: result });

      toast.success("Claimers updated successfully");
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response.data.message || "something went wrong while updating the claimers"
      )
    }
  };

  return (
    <>
      <Outlet context={{ ...state, setState, connectWallet, updateClaimers }} />
    </>
  );
}
